import { makeStyles } from '@material-ui/core/styles'
import { useState, useRef, useEffect } from 'react'
import { TextField } from '@material-ui/core'

import IconButton from '@/components/ui/icon-button'

import { useLocation, useNavigate } from 'react-router-dom'
import useConversationsStore from '@/stores/useConversationsStore'

const useStyles = makeStyles((theme) => ({
  headerButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1),
    borderBottom: `1px dashed ${theme.palette.grey[400]}`,
  },
  left: {
    display: 'flex',
    alignItems: 'center',
  },
  right: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    overflow: 'hidden',
  },
  searchInput: {
    transition: 'all 0.3s ease-in-out',
    transformOrigin: 'right center',
    '& .MuiOutlinedInput-root': {
      height: '36px',
      '& fieldset': {
        borderColor: theme.palette.grey[300],
      },
      '&:hover fieldset': {
        borderColor: theme.palette.grey[400],
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: theme.spacing(1),
      fontSize: '14px',
    },
  },
  searchInputVisible: {
    transform: 'scaleX(1)',
    opacity: 1,
    width: '100%',
  },
  searchInputHidden: {
    transform: 'scaleX(0)',
    opacity: 0,
    width: '100%',
    pointerEvents: 'none',
  },
}))

const HeaderButtons = () => {
  const classes = useStyles()
  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const searchInputRef = useRef<HTMLInputElement>(null)

  const navigate = useNavigate()
  const location = useLocation()

  const toggleSidebar = useConversationsStore((state) => state.toggleSidebar)
  const setSearchTerm = useConversationsStore((state) => state.setSearchTerm)
  const setPendingFirstMessage = useConversationsStore((state) => state.setPendingFirstMessage)

  const handleAddClick = () => {
    const basePath = location.pathname.split('/plus')[0]
    navigate(basePath + '/plus')
    setPendingFirstMessage(null)
  }

  const handleSearchClick = () => {
    setIsSearchVisible(true)
  }

  const handleSearchBlur = () => {
    setTimeout(() => {
      setIsSearchVisible(false)
      setSearchValue('')
      setSearchTerm(null)
    }, 150)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
    setSearchTerm(event.target.value)
  }

  useEffect(() => {
    if (isSearchVisible && searchInputRef.current) {
      searchInputRef.current?.focus()
    }
  }, [isSearchVisible])

  return (
    <div className={classes.headerButtons}>
      <div className={classes.left}>
        <IconButton icon="menu_open" onClick={toggleSidebar} />
      </div>
      <div className={classes.searchContainer}>
        <TextField
          inputRef={searchInputRef}
          className={`${classes.searchInput} ${
            isSearchVisible ? classes.searchInputVisible : classes.searchInputHidden
          }`}
          variant="outlined"
          placeholder="Search conversations..."
          value={searchValue}
          onChange={handleSearchChange}
          onBlur={handleSearchBlur}
          size="small"
        />
      </div>
      <div className={classes.right}>
        {!isSearchVisible && <IconButton icon="search" onClick={handleSearchClick} />}
        <IconButton icon="add" onClick={handleAddClick} />
      </div>
    </div>
  )
}

export default HeaderButtons
